import * as Dialog from '@radix-ui/react-dialog';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { dialogVariants } from '@/styles/dialog';

import { Overlay } from '@/ui/Dialog';

import { AnimatedDiv } from '@/utils/animated-div';

import { ModalProps } from '..';

const Y = 100;

const AnimatedOverlay = animated(Overlay);
const AnimatedContent = animated(Dialog.Content);

export interface BottomPanelProps extends Omit<ModalProps, 'onOpenChange'> {
  onOpenChange: (open: boolean) => void;
  variant?: 'DEFAULT' | 'LARGE';
  allowGestures?: boolean;
}

const config = {
  tension: 400,
  friction: 49,
  mass: 2,
};

type ScrollState = 'scrolling' | 'top' | 'bottom';

export const BottomPanelModal = ({
  children,
  isOpen: desiredState,
  onClose,
  onOpenChange,
  variant = 'DEFAULT',
  allowGestures = true,
}: BottomPanelProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(desiredState);
  const scrollState = useRef<ScrollState>('top');
  const touchStartScrollState = useRef<ScrollState>('top');
  const handleRef = useRef<HTMLDivElement>(null);

  const [{ y }, springRef] = useSpring(() => ({
    y: Y,
    config,
  }));

  const open = useCallback(
    ({ canceled }: { canceled: boolean }) => {
      // when cancel is true, it means that the user passed the upwards threshold
      // so we change the spring config to create a nice wobbly effect
      if (!canceled) springRef.set({ y: 100 });
      springRef.start({
        y: 0,
        immediate: false,
        config,
      });
      setIsOpen(true);
    },
    [springRef],
  );
  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose();
    springRef.set({ y: 0 });
  }, [onClose, springRef]);

  const close = useCallback(
    (velocity = 0) => {
      springRef.start({
        y: 100,
        immediate: false,
        config: { ...config, velocity },
      });
      setTimeout(() => handleClose(), 300);
    },
    [handleClose, springRef],
  );

  useEffect(() => {
    if (isOpen === desiredState) return;
    if (desiredState) {
      open({ canceled: false });
    } else {
      close();
    }
  }, [close, desiredState, isOpen, open]);

  const bindGesture = useDrag(
    ({ event, last, velocity: [, vy], direction: [, dy], movement: [, my] }) => {
      const sheetHeight = contentRef?.current?.getBoundingClientRect?.()?.height ?? 1;

      // If the user is on the last touch, we close the modal if the following rules are fulfilled:
      // 1. The gesture started with a scrollState of "top"
      // 2. The user did a fast flick downwards or the user moved the modal down more than 50%
      if (last) {
        if (
          touchStartScrollState.current === 'top' &&
          ((vy > 0.5 && dy > 0 && my !== 0) || my > sheetHeight * 0.5)
        ) {
          close(vy);
          return;
        }
        open({ canceled: true });
        return;
      }
      if (vy !== 0 || my !== 0 || dy !== 0) event.stopPropagation();

      if (!last) {
        springRef.start({ y: (my / sheetHeight) * 100, immediate: true });
      }
    },
    {
      from: () => [0, y.get()],
      filterTaps: true,
      bounds: { top: 0 },
      rubberband: true,
    },
  );

  const gestureHandlers = isOpen && allowGestures ? bindGesture() : {};

  function handleKeyEscape(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') onClose();
  }
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <AnimatedOverlay className="bg-primitive-alphalight-800 pointer-events-none" />
        <div className="fixed top-0 left-0 right-0 bottom-0 flex items-end tablet:items-center pointer-events-none z-1000">
          <div className="w-full m-0 tablet:m-auto bottom-0 tablet:bottom-auto">
            <AnimatedDiv
              className="h-10 pointer-none fixed -bottom-10 w-full bg-special-background-modal"
              style={{
                transform: y.to((y) => `translateY(${y / 3}%)`),
              }}
            />
            <AnimatedContent
              className={dialogVariants[`${variant}_BOTTOM_PANEL`]()}
              onKeyDown={handleKeyEscape}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
              }}
              style={{
                transform: y.to((y) => `translateY(${y}%)`),
              }}
              ref={contentRef}
            >
              <div
                className="h-1.5 bg-token-gray-400 dark:bg-token-alphalight-200 tablet:hidden left-[50%] absolute top-2 w-16 rounded-xs z-11 transform -translate-x-[50%]"
                ref={handleRef}
                {...gestureHandlers}
                // The handle is a dedicated drag target, so disable native touch handling.
                style={{ touchAction: 'none' }}
              />
              <div
                className="overflow-y-auto max-h-[90dvh] relative"
                {...gestureHandlers}
                // Allow native vertical scrolling of the content while keeping the
                // drag-to-close gesture (handled at the scroll-top boundary). Setting
                // an explicit touch-action also silences the @use-gesture dev warning.
                style={{ touchAction: 'pan-y' }}
                onScrollCapture={(e) => {
                  const { offsetHeight, scrollHeight, scrollTop } = e.currentTarget;
                  if (scrollTop === 0) {
                    scrollState.current = 'top';
                  } else if (offsetHeight + scrollTop === scrollHeight) {
                    scrollState.current = 'bottom';
                  } else {
                    scrollState.current = 'scrolling';
                  }
                }}
              >
                {children}
              </div>
            </AnimatedContent>
            <AnimatedDiv
              className="h-10 pointer-none fixed -bottom-10 w-full bg-special-background-modal"
              style={{
                transform: y.to((y) => `translateY(${y}%)`),
              }}
            />
          </div>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
